import React from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import type { DetailSearchModalProps } from '../../../types/admin';

export const DetailSearchModal: React.FC<DetailSearchModalProps> = ({
  open,
  onClose,
  onSearch,
  keyword,
  onKeywordChange,
  keywordLabel = 'キーワード',
  keywordPlaceholder = 'キーワードを入力',
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  selectLabel,
  selectValue,
  onSelectChange,
  selectOptions,
}) => {
  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '42px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            width: '800px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box sx={{ px: 4, pt: 4, pb: 3 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 600, mb: 3 }}>
              詳細検索
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label={keywordLabel}
                  value={keyword}
                  onChange={(e) => onKeywordChange(e.target.value)}
                  placeholder={keywordPlaceholder}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <DatePicker
                    value={startDate}
                    onChange={onStartDateChange}
                    format="yyyy/MM/dd (eee)"
                    label="登録日（開始）"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                          },
                        },
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: '14px', mt: 1.75 }}>〜</Typography>
                  <DatePicker
                    value={endDate}
                    onChange={onEndDateChange}
                    format="yyyy/MM/dd (eee)"
                    label="登録日（終了）"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: '#FFFFFF',
                          },
                        },
                      },
                    }}
                  />
                </Box>

                <FormControl fullWidth>
                  <InputLabel>{selectLabel}</InputLabel>
                  <Select
                    value={selectValue}
                    onChange={(e) => onSelectChange(e.target.value)}
                    label={selectLabel}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      },
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                    }}
                  >
                    {selectOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </LocalizationProvider>
          </Box>
          <Box sx={{ px: 4, pb: 4, pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={onSearch}
              variant="contained"
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#1B76D2',
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '16px',
                '&:hover': {
                  backgroundColor: '#1565C0',
                },
              }}
            >
              上記内容で検索
            </Button>
          </Box>
        </Box>

        <Button
          onClick={onClose}
          sx={{
            backgroundColor: '#666666',
            color: '#FFFFFF',
            borderRadius: '8px',
            textTransform: 'none',
            px: 4,
            py: 1.5,
            '&:hover': {
              backgroundColor: '#555555',
            },
          }}
        >
          閉じる
        </Button>
      </Box>
    </Box>
  );
};

